import React, { useState } from 'react';
import { ClientConfig } from '../types/profile';
import { useLocalization } from '../engine/localization/context';
import { downloadVCard } from '../engine/actions/vcard';
import { shareProfile } from '../engine/actions/share';
import { UserPlus, Share2, QrCode, MessageSquare, Check } from 'lucide-react';

interface SaveAndShareProps {
  config: ClientConfig;
  onOpenQr: () => void;
  onOpenContact: () => void;
}

export const SaveAndShare: React.FC<SaveAndShareProps> = ({
  config,
  onOpenQr,
  onOpenContact,
}) => {
  const { locale, resolve, t } = useLocalization();
  const [copiedNotification, setCopiedNotification] = useState(false);

  const handleDownloadVCard = () => {
    downloadVCard(config, locale);
  };

  const handleShare = async () => {
    const title = resolve(config.identity.name);
    const text = `${t('share.text')} - ${title}`;
    const url = config.profileUrl || window.location.href;

    const result = await shareProfile({ title, text, url });
    if (result === 'copied') {
      setCopiedNotification(true);
      setTimeout(() => setCopiedNotification(false), 2500);
    }
  };

  return (
    <section aria-label="Save and Share Hub" className="my-5 sm:my-6 space-y-2.5 sm:space-y-3">
      {/* Primary Action: Save Contact to Phone VCF */}
      {config.features.vCardDownload && (
        <button
          type="button"
          onClick={handleDownloadVCard}
          className="w-full flex items-center justify-between p-3.5 sm:p-4 rounded-xl sm:rounded-2xl bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-400 hover:to-sky-500 text-white font-bold shadow-[0_8px_25px_rgba(2,132,199,0.35)] transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] group"
        >
          <div className="flex items-center gap-2.5 sm:gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <UserPlus className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
            </div>
            <div className="text-start">
              <div className="text-xs sm:text-sm font-bold">{t('action.saveContact')}</div>
              <div className="text-[10px] sm:text-[11px] text-sky-100 font-normal opacity-90 leading-tight">
                {t('action.saveContactDesc')}
              </div>
            </div>
          </div>
          <span className="text-[11px] sm:text-xs font-semibold px-2 py-1 rounded bg-white/20 shrink-0">VCF</span>
        </button>
      )}

      {/* Grid for QR Modal & Share buttons */}
      <div className="grid grid-cols-2 gap-2 xs:gap-2.5 sm:gap-3">
        {/* QR Code trigger */}
        {config.features.qrModal && (
          <button
            type="button"
            onClick={onOpenQr}
            className="flex items-center justify-center gap-2 p-3.5 rounded-2xl bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/60 hover:border-sky-500/40 text-slate-200 hover:text-white transition-all duration-200 text-xs font-bold shadow-sm"
          >
            <QrCode className="w-4 h-4 text-sky-400" />
            <span>{t('action.qr')}</span>
          </button>
        )}

        {/* Share profile trigger with copy indicator */}
        {config.features.shareProfile && (
          <div className="relative">
            <button
              type="button"
              onClick={handleShare}
              className="w-full flex items-center justify-center gap-2 p-3.5 rounded-2xl bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/60 hover:border-sky-500/40 text-slate-200 hover:text-white transition-all duration-200 text-xs font-bold shadow-sm"
            >
              {copiedNotification ? (
                <Check className="w-4 h-4 text-emerald-400 animate-scale-in" />
              ) : (
                <Share2 className="w-4 h-4 text-sky-400" />
              )}
              <span>{copiedNotification ? t('action.copied') : t('action.share')}</span>
            </button>

            {copiedNotification && (
              <div className="absolute -top-9 left-1/2 -translate-x-1/2 px-2.5 py-1 bg-emerald-600 text-white text-[11px] font-semibold rounded-lg shadow-lg whitespace-nowrap animate-fade-in pointer-events-none z-20">
                {t('action.copied')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Contact Form Trigger */}
      {config.features.contactForm && (
        <button
          type="button"
          onClick={onOpenContact}
          className="w-full flex items-center justify-center gap-2 p-3.5 rounded-2xl bg-gradient-to-r from-amber-500/20 to-amber-600/20 hover:from-amber-500/30 hover:to-amber-600/30 border border-amber-500/40 text-amber-300 hover:text-amber-200 text-xs font-bold transition-all shadow-sm"
        >
          <MessageSquare className="w-4 h-4 text-amber-400" />
          <span>{t('action.contactForm')}</span>
        </button>
      )}
    </section>
  );
};

